// src/utilities/dashboards.ts
interface Role {
  id: number
  defaultDashboard: string
  dashboardsAllowed: string[]
  roleWeight: number
}

interface Dashboard {
  id: string
  title: string
  route: string
  localeKey: string
  localePluralization: number
  rolesAllowed: number[]
  isVisible: boolean
}

export const roles: Role[] = [
  {
    id: 1, // Admin
    defaultDashboard: 'retain-admin',
    dashboardsAllowed: ['all'],
    roleWeight: 1
  },
  {
    id: 5, // Leader
    defaultDashboard: 'retain-company',
    dashboardsAllowed: ['retain-company', 'dealership'],
    roleWeight: 2
  },
  {
    id: 4, // Manager
    defaultDashboard: 'retain-call-center',
    dashboardsAllowed: ['retain-call-center-agent', 'retain-call-center', 'retain-call-center-team'],
    roleWeight: 3
  },
  {
    id: 6, // Dealer Development Manager
    defaultDashboard: 'retain-company',
    dashboardsAllowed: ['retain-company', 'dealership', 'dealership-sales-executive'],
    roleWeight: 4
  },
  {
    id: 3, // Team Leader
    defaultDashboard: 'retain-call-center',
    dashboardsAllowed: ['retain-call-center-agent', 'retain-call-center'],
    roleWeight: 5
  },
  {
    id: 2, // Call Agent
    defaultDashboard: 'retain-call-center-agent',
    dashboardsAllowed: ['retain-call-center-agent'],
    roleWeight: 6
  },
  {
    id: 30, // Manufacturer - Regional
    defaultDashboard: 'dealership',
    dashboardsAllowed: ['dealership'], // TODO: add dealership-group
    roleWeight: 30
  },
  {
    id: 31, // Manufacturer - National
    defaultDashboard: 'dealership',
    dashboardsAllowed: ['dealership'], // TODO: add dealership-group
    roleWeight: 31
  },
  {
    id: 12, // Group Sales Manager
    defaultDashboard: 'dealership',
    dashboardsAllowed: ['dealership'], // TODO: add dealership-group
    roleWeight: 32
  },
  {
    id: 11, // Sales Manager
    defaultDashboard: 'dealership',
    dashboardsAllowed: ['dealership'], // TODO: add sales-executive dashboard once able to filter by dealership
    roleWeight: 33
  },
  {
    id: 10, // Sales Executive
    defaultDashboard: 'dealership-sales-executive',
    dashboardsAllowed: ['dealership-sales-executive'],
    roleWeight: 34
  },
  {
    id: 34, // SaaS Call Agent
    defaultDashboard: 'retain-call-center-agent',
    dashboardsAllowed: ['retain-call-center-agent'],
    roleWeight: 6
  },
  {
    id: 37, // SaaS Team Leader
    defaultDashboard: 'retain-call-center',
    dashboardsAllowed: ['retain-call-center-agent', 'retain-call-center'],
    roleWeight: 7
  }
]

/**
 * Get the roles allowed to access a dashboard
 * @param dashboard - The dashboard id
 * @returns {number[]} - The role ids allowed to access the dashboard
 */
export const getRolesForDashboard = (dashboard: string): number[] => {
  return roles
    .filter(role => {
      return role.dashboardsAllowed.includes(dashboard) || role.dashboardsAllowed.includes('all')
    })
    .map(role => role.id)
}

/**
 * The dashboards
 * @type {Dashboard[]}
 */
export const dashboards: Dashboard[] = [
  {
    id: 'retain-admin',
    title: 'Admin',
    route: '/dashboard/admin',
    localeKey: 'ui.entities.user.role.admin',
    localePluralization: 1,
    rolesAllowed: getRolesForDashboard('retain-admin'),
    isVisible: true
  },
  {
    id: 'retain-call-center-agent',
    title: 'Call Agent',
    route: '/dashboard/call-center/agent',
    localeKey: 'ui.entities.user.role.callAgent',
    localePluralization: 1,
    rolesAllowed: getRolesForDashboard('retain-call-center-agent'),
    isVisible: true
  },
  // {
  //   id: 'retain-call-center-team',
  //   title: 'Team',
  //   route: '/dashboard/call-center/team',
  //   component: './dashboards/call-center/TeamDashboard.vue',
  //   componentFolder: 'call-center',
  //   componentName: 'TeamDashboard',
  //   localeKey: 'ui.entities.team.label',
  //   localePluralization: 1,
  //   rolesAllowed: getRolesForDashboard('retain-call-center-team')
  // },
  {
    id: 'retain-call-center',
    title: 'Call Center',
    route: '/dashboard/call-center',
    localeKey: 'ui.entities.dashboards.callCenter',
    localePluralization: 1,
    rolesAllowed: getRolesForDashboard('retain-call-center'),
    isVisible: true
  },
  {
    id: 'retain-company',
    title: 'Company',
    route: '/dashboard/company',
    localeKey: 'ui.common.company',
    localePluralization: 1,
    rolesAllowed: getRolesForDashboard('retain-company'),
    isVisible: true
  },
  {
    id: 'dealership-sales-executive',
    title: 'Sales Executive',
    route: '/dashboard/dealership/sales-executive',
    localeKey: 'ui.entities.user.role.salesExecutive',
    localePluralization: 1,
    rolesAllowed: getRolesForDashboard('dealership-sales-executive'),
    isVisible: true
  },
  {
    id: 'dealership',
    title: 'Dealership',
    route: '/dashboard/dealership',
    localeKey: 'ui.entities.dealership',
    localePluralization: 1,
    rolesAllowed: getRolesForDashboard('dealership'),
    isVisible: true
  },
  {
    id: 'dealership-group',
    title: 'Group',
    route: '/dashboard/dealership/group',
    localeKey: 'ui.entities.group',
    localePluralization: 1,
    rolesAllowed: getRolesForDashboard('dealership-group'),
    isVisible: true
  }
]

/**
 * Get the dashboards allowed to access a role
 * @param roleIds - The role ids
 * @returns {Dashboard[]} - The dashboards allowed to access the role
 */
export const getDashboardsForRoles = (roleIds: number[]): Dashboard[] => {
  return dashboards.filter(dashboard => {
    return roleIds.some(roleId => dashboard.rolesAllowed.includes(roleId))
  })
}

/**
 * Get the default dashboard for a role
 * @param roleIds - The role ids
 * @returns {Dashboard | undefined} - The default dashboard for the role
 */
export const getDefaultDashboardForRoles = (roleIds: number[]): Dashboard | undefined => {
  const rolesToCheck = roles.filter(role => roleIds.includes(role.id))
  const sortedRoles = rolesToCheck.sort((a, b) => {
    return a.roleWeight - b.roleWeight
  })
  return dashboards.find(dashboard => dashboard.id === sortedRoles[0].defaultDashboard)
}

/**
 * Check if a role has access to more than one dashboard
 * @param roleIds - The role ids
 * @returns {boolean} - True if the role has access to more than one dashboard
 */
export const hasMultipleDashboards = (roleIds: number[]): boolean => {
  return getDashboardsForRoles(roleIds).length > 1
}

/**
 * Get a dashboard by its route
 * @param route - The dashboard route
 * @returns {Dashboard | undefined} - The dashboard object
 */
export const getDashboardByRoute = (route: string): Dashboard | undefined => {
  return dashboards.find(dashboard => dashboard.route === route)
}
